'use client';

import { useRef, useEffect, useState } from 'react';
import { cn } from '@/shadcn/lib/utils';
import { ChevronDown } from 'lucide-react';

export interface SelectOption<T extends string = string> {
    value: T;
    label: string;
}

interface Props<T extends string = string> {
    options: SelectOption<T>[];
    value: T | null;
    onChange: (value: T) => void;
    placeholder?: string;
    iconLeft?: React.ReactNode;
    className?: string;
}

export function SelectPopover<T extends string = string>({
    options,
    value,
    onChange,
    placeholder = 'Vybrať',
    iconLeft,
    className,
}: Props<T>) {
    const [open, setOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const selectedOption = options.find((o) => o.value === value) ?? null;

    useEffect(() => {
        const handlePointerDownOutside = (event: PointerEvent) => {
            const target = event.target as Node | null;
            if (!containerRef.current || !target) return;
            if (!containerRef.current.contains(target)) setOpen(false);
        };
        document.addEventListener('pointerdown', handlePointerDownOutside);
        return () => document.removeEventListener('pointerdown', handlePointerDownOutside);
    }, []);

    return (
        <div ref={containerRef} className={cn('relative z-[100]', className)}>
            <button
                type="button"
                className={cn(
                    'w-full h-[40px] text-[13px] border flex items-center bg-white px-[8px] rounded-[30px] gap-[5px] cursor-pointer',
                    'xl:h-[45px]',
                    'transition-colors duration-150',
                    open && 'border-[#ddd]',
                )}
                onClick={() => setOpen((v) => !v)}
            >
                {iconLeft && (
                    <span className="flex items-center pl-[4px]">{iconLeft}</span>
                )}

                <span className="flex-1 flex items-center min-w-0 px-[2px]">
                    {selectedOption ? (
                        <span className="flex items-center bg-[#f8ebb4] px-[9px] py-[3px] rounded-[15px] whitespace-nowrap">
                            <small className="text-[11px] font-[600]">{selectedOption.label}</small>
                        </span>
                    ) : (
                        <span className="text-muted-foreground text-[12px] font-[500] truncate">
                            {placeholder}
                        </span>
                    )}
                </span>

                <ChevronDown
                    className={cn(
                        'size-[14px] text-[#888] shrink-0 mr-[4px] transition-transform duration-200',
                        open && 'rotate-180',
                    )}
                />
            </button>

            {open && (
                <div className="absolute left-0 top-full mt-[6px] w-full z-[999] bg-white rounded-[20px] shadow-[0_12px_40px_rgba(0,0,0,0.14)]">
                    <ul className="p-[8px] flex flex-col gap-[2px]">
                        {options.map((opt) => (
                            <li
                                key={opt.value}
                                className={cn(
                                    'px-[12px] py-[9px] text-[13px] font-[500] rounded-[10px] cursor-pointer',
                                    'transition-colors duration-150',
                                    value === opt.value
                                        ? 'bg-[#f8ebb4]'
                                        : 'hover:bg-[#f5f5f5]',
                                )}
                                onClick={() => {
                                    onChange(opt.value);
                                    setOpen(false);
                                }}
                            >
                                {opt.label}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
